import { Controller } from "stimulus"

export default class extends Controller {
    static targets = [ "container", "input" ]

    connect() {
       console.log("Providers list controller connected!")
       console.log("Container target exists:", this.hasContainerTarget)
       console.log("Input target exists:", this.hasInputTarget)
       this.selectedIndex = -1
       this.debounceTimeout = null
       this.currentAbortController = null
       this.lastRequestId = 0
    }

    findProviders(event) {
      console.log("findProviders called with value:", event.target.value, "event type:", event.type)
      
      // Handle arrow key navigation - ONLY on keydown to prevent double execution
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && event.type === 'keydown') {
        event.preventDefault()
        this.navigateList(event.key === 'ArrowDown' ? 1 : -1)
        return
      }
      
      // Handle Enter key - only on keydown to prevent form submission
      if (event.key === 'Enter' && event.type === 'keydown') {
        event.preventDefault()
        event.stopPropagation()
        this.selectCurrentItem()
        return
      }
      
      // Handle Escape key
      if (event.key === 'Escape') {
        this.closeList()
        return
      }
      
      // Only proceed with search on actual input changes, not navigation keys
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
        return
      }
      
      if (!this.hasContainerTarget) {
        console.error("Container target not found")
        return
      }
      
      const query = event.target.value.trim()
      
      // Clear previous timeout
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }
      
      // Cancel previous request
      if (this.currentAbortController) {
        this.currentAbortController.abort()
      }
      
      // If query is too short, hide the dropdown
      if (query.length < 2) {
        this.closeList()
        return
      }
      
      // Reset selection when typing
      this.selectedIndex = -1
      
      // Debounce the API call by 300ms
      this.debounceTimeout = setTimeout(() => {
        this.performSearch(query)
      }, 300)
    }

    performSearch(query) {
      // Create new AbortController for this request
      this.currentAbortController = new AbortController()
      const requestId = ++this.lastRequestId
      
      fetch(`/providers/list?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {'Accept': 'text/plain' },
        signal: this.currentAbortController.signal
      })
      .then(response => {
        // Check if this is still the most recent request
        if (requestId !== this.lastRequestId) {
          console.log("Ignoring outdated response")
          return
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        return response.text()
      })
      .then((data) => {
        // Double-check this is still the most recent request
        if (requestId !== this.lastRequestId) {
          console.log("Ignoring outdated response data")
          return
        }
        
        if (this.hasContainerTarget) {
          console.log("Showing autocomplete dropdown")
          this.containerTarget.classList.remove("d-none")
          this.containerTarget.innerHTML = data
          console.log("Dropdown content:", data)
          this.updateListStyles()
          
          // Add class to body for CSS fallback
          document.body.classList.add('autocomplete-open')
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.log("Request was cancelled")
        } else {
          console.error("Error fetching providers:", error)
        }
      })
    }

    navigateList(direction) {
      if (!this.hasContainerTarget || this.containerTarget.classList.contains("d-none")) {
        return
      }
      
      const items = this.containerTarget.querySelectorAll('.provider-item')
      if (items.length === 0) return
      
      // Remove previous selection
      items.forEach(item => item.classList.remove('selected'))
      
      // Update selected index
      this.selectedIndex += direction
      
      // Handle boundaries
      if (this.selectedIndex < -1) {
        this.selectedIndex = items.length - 1
      } else if (this.selectedIndex >= items.length) {
        this.selectedIndex = -1
      }
      
      // Highlight selected item
      if (this.selectedIndex >= 0) {
        const selectedItem = items[this.selectedIndex]
        selectedItem.classList.add('selected')
        selectedItem.scrollIntoView({ block: 'nearest' })
      }
    }

    selectCurrentItem() {
      if (!this.hasContainerTarget || this.selectedIndex < 0) {
        // If no item is selected, check if search is for company/provider before submitting
        this.checkAndSubmitForm()
        return
      }
      
      const items = this.containerTarget.querySelectorAll('.provider-item')
      if (items[this.selectedIndex]) {
        // Get the clean title from data attribute
        const selectedItem = items[this.selectedIndex]
        const providerName = selectedItem.getAttribute('data-title') || 
                            selectedItem.querySelector('.item-title')?.innerText?.trim() ||
                            selectedItem.innerText.trim()
        
        console.log("Selecting item:", providerName)
        
        // Set the value directly and submit
        this.inputTarget.value = providerName
        this.closeList()
        
        // Check if company/provider before submitting
        this.checkAndSubmitForm()
      } else {
        // Fallback: check if company/provider before submitting
        this.checkAndSubmitForm()
      }
    }

    checkAndSubmitForm() {
      console.log('checkAndSubmitForm called')
      const searchQuery = this.inputTarget.value.trim()
      console.log('Search query:', searchQuery)
      
      if (!searchQuery) {
        const form = this.inputTarget.closest('form')
        if (form) window.location.href = form.action
        return
      }

      // Always redirect to portal with search query
      console.log('Redirecting to portal with query:', searchQuery)
      window.location.href = `/portal?search_query=${encodeURIComponent(searchQuery)}`
    }

    updateListStyles() {
      // Ensure proper z-index and positioning
      if (this.hasContainerTarget) {
        // Let CSS handle the z-index and positioning
        this.containerTarget.style.position = 'absolute'
        
        // Add event listeners to items for better UX
        const items = this.containerTarget.querySelectorAll('.provider-item')
        items.forEach((item, index) => {
          // Add hover effect
          item.addEventListener('mouseenter', () => {
            this.selectedIndex = index
            items.forEach(i => i.classList.remove('selected'))
            item.classList.add('selected')
          })
          
          item.addEventListener('mouseleave', () => {
            item.classList.remove('selected')
            this.selectedIndex = -1
          })
        })
      }
    }

    setProvider(event) {
      if (!this.hasInputTarget) {
        console.error("Input target not found")
        return
      }
      
      // Get the title from data attribute to ensure we only get the clean title
      const providerName = event.currentTarget.getAttribute('data-title') || 
                          event.currentTarget.querySelector('.item-title')?.innerText?.trim() ||
                          event.currentTarget.innerText.trim()
      
      this.inputTarget.value = providerName
      this.closeList()
      
      // Navigate to the search results - use the current form's action or submit
      const form = this.inputTarget.closest('form')
      if (form) {
        form.submit()
      }
    }

    closeList() {
      if (!this.hasContainerTarget) {
        console.error("Container target not found")
        return
      }
      
      // Cancel any pending requests
      if (this.currentAbortController) {
        this.currentAbortController.abort()
        this.currentAbortController = null
      }
      
      // Clear any pending debounced calls
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
        this.debounceTimeout = null
      }
      
      this.containerTarget.classList.add("d-none")
      this.selectedIndex = -1
      
      // Remove body class for CSS fallback
      document.body.classList.remove('autocomplete-open')
    }

    disconnect() {
      // Clean up when controller is disconnected
      if (this.currentAbortController) {
        this.currentAbortController.abort()
      }
      
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }
      
      // Remove body class for CSS fallback
      document.body.classList.remove('autocomplete-open')
    }
}